import React, { Component, ReactNode } from 'react';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import LanguageUtil from '../../util/LanguageUtil';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import ActionService from '../../service/ActionService';
import { ActionJsonModel } from '../../domain/collection/rule/action/ActionDomains';
import { ActionState } from '../../redux/reducers/ActionReducer';
import ItemStatusComponent from '../ItemStatusComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;

class ActionListViewerComponent extends Component<Props> {
  private actionService: ActionService = ServiceContainer.getInstance().get(ServiceNames.ACTION);

  public render(): ReactNode {
    const {
      views,
      actions,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    if (!(views && actions && views.length > 0 && actions.length > 0)) {
      return <EmptyComponent type={ComponentName.ACTION} />;
    }
    return (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={actions}
          columns={this.getColumns()}
          selectionMode='checkbox'
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          onRowClick={this.openEditor}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          componentName={ComponentName.ACTION}
          openNewTab={true}
          onSortChange={onSortChange}
        />
        <ListDraggableItem itemType={ComponentName.ACTION} currentModal={this.props.currentModal} />
      </div>
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.action.table.name', defaultMessage: 'Name' }),
        className: 'w-1/8',
        copyable: true,
        onCopy: (e: ActionJsonModel) => {
          return e.name;
        },
        body: (record: ActionJsonModel) => {
          const { views } = this.props;
          const view: FolderView | undefined = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={15} text={record.name} data-testid={`${ComponentName.ACTION}_name_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: 'pages.action.table.status', defaultMessage: 'Status' }),
        className: 'w-1/8',
        body: (record: ActionJsonModel) => {
          return (
            <ItemStatusComponent status={record.stage} containerClassName={'justify-start'} data-testid={`${ComponentName.ACTION}_stage_text_${record.name}`} />
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.action.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[18%]',
        body: (record: ActionJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper
                clipSize={35}
                text={
                  record.description ||
                  LanguageUtil.get({ id: 'pages.action.table.noDescription', defaultMessage: 'This Action does not have a description yet.' })
                }
                showEllipsis={true}
                data-testid={`${ComponentName.ACTION}_description_text_${record.name}`}
              />
            </div>
          );
        },
      },
      {
        field: 'template',
        header: LanguageUtil.get({ id: 'pages.action.table.template', defaultMessage: 'Template' }),
        className: 'w-1/8',
        body: (record: ActionJsonModel) => {
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div />
              <CustomTableContentWrapper text={record.template} data-testid={`${ComponentName.ACTION}_template_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'pages.action.table.type', defaultMessage: 'Type' }),
        className: 'w-1/8',
        body: (record: ActionJsonModel) => {
          return (
            <div className={'flex items-start text-xs 2xl:text-md'}>
              <div />
              <CustomTableContentWrapper text={record.type} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.action.table.createdAt', defaultMessage: 'Created At' }),
        className: 'w-1/8',
        body: (record: ActionJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.action.table.updatedAt', defaultMessage: 'Updated At' }),
        className: 'w-1/8',
        body: (record: ActionJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Action')) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.action.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: ActionJsonModel) => {
    const { permissions } = this.props;

    const actions: ReactNode[] = [];

    const permission = permissions[record.name];

    const allowed = ['Edit', 'Admin'];
    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.ACTION, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.ACTION, [record.name], () => this.actionService.undeploy(record)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
          data-testid={`${ComponentName.ACTION}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.actionService.remove(record),
              message: LanguageUtil.get({ id: 'messages.confirm.remove1', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.ACTION}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.actionService.deploy(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.ACTION}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );

    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openAction } = this.props;
    if (e.data) {
      openAction(e.data.name);
    }
  };
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  component: state.component as ComponentState,
});

const mapDispatchToProps = {};

interface Props extends ReduxProps {
  views: any;
  actions: ActionJsonModel[];
  openAction: (name: string) => void;
  currentModal: any;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(ActionListViewerComponent);
